import { ART_WINDOW, CARD_BODY, CARD_SHADOW } from '#shared/core/card-geometry';
import stickerSvg from '@/assets/brand/tts-sticker.svg?raw';
import { halftonePaths, INK } from './backdrops';
import { FONT } from './CardSvg';
import { copy } from './copy';

/**
 * The back of the pack, shown while the reveal is sealed and torn. Lavender, a white halftone
 * field, the TTS sticker wordmark inlined from the brand vector, and a perforation line under
 * the strip that flies off. Same 500 x 700 board as the face.
 */

const WORDMARK = (() => {
  const m = /viewBox="([^"]+)"[^>]*>([\s\S]*)<\/svg>\s*$/.exec(stickerSvg);
  return { viewBox: m?.[1] ?? '0 0 320 140', inner: m?.[2] ?? '' };
})();
const VB = WORDMARK.viewBox.split(/\s+/).map(Number);
const WM_W = 340;
const WM_H = WM_W * ((VB[3] ?? 140) / (VB[2] ?? 320));
const STRIP_H = 120;

export interface CardBackSvgProps {
  uid: string;
  shadowColor: string;
  tearing: boolean;
  label: string;
}

function BackArt({ uid, shadowColor, withShadow }: { uid: string; shadowColor: string; withShadow: boolean }) {
  const b = CARD_BODY;
  const inner = { x: 26, y: 26, w: 448, h: 652 };
  return (
    <g>
      {withShadow && (
        <rect
          x={b.x + CARD_SHADOW.dx}
          y={b.y + CARD_SHADOW.dy}
          width={b.w}
          height={b.h}
          rx={b.rx}
          style={{ fill: shadowColor }}
        />
      )}
      <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={b.rx} fill="#e2a4ff" stroke={INK} strokeWidth={b.stroke} />
      <rect
        x={inner.x}
        y={inner.y}
        width={inner.w}
        height={inner.h}
        rx={4}
        fill="#edd0e4"
        stroke={INK}
        strokeWidth={5}
      />
      <g fill="#ffffff" clipPath={`url(#${uid}-inner)`}>
        {halftonePaths(30, inner, 2, 6).map(({ r, d }) => (
          <path key={r} d={d} />
        ))}
      </g>
      <svg
        x={250 - WM_W / 2}
        y={210}
        width={WM_W}
        height={WM_H}
        viewBox={WORDMARK.viewBox}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: our own brand vector, inlined at build time
        dangerouslySetInnerHTML={{ __html: WORDMARK.inner }}
      />
      <g transform={`translate(250 ${ART_WINDOW.y + 400}) skewX(-7)`}>
        <rect x={-130} y={-30} width={260} height={60} rx={3} fill={INK} />
        <text
          x={0}
          y={12}
          textAnchor="middle"
          fontFamily={FONT}
          fontWeight={900}
          fontStyle="italic"
          fontSize={34}
          fill="#ffffff"
        >
          {copy.reveal.tapToRip.toLocaleUpperCase()}
        </text>
      </g>
      <text
        x={250}
        y={110}
        textAnchor="middle"
        fontFamily={FONT}
        fontWeight={900}
        fontStyle="italic"
        fontSize={30}
        fill={INK}
      >
        {copy.reveal.sealedBack.toLocaleUpperCase()}
      </text>
    </g>
  );
}

export function CardBackSvg({ uid, shadowColor, tearing, label }: CardBackSvgProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 500 700"
      role="img"
      aria-label={label}
      data-testid="player-card-back"
      style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
    >
      <defs>
        <clipPath id={`${uid}-inner`}>
          <rect x={26} y={26} width={448} height={652} />
        </clipPath>
        <clipPath id={`${uid}-strip`}>
          <rect x={-40} y={-40} width={580} height={STRIP_H + 40} />
        </clipPath>
        <clipPath id={`${uid}-rest`}>
          <rect x={-40} y={STRIP_H} width={580} height={700} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${uid}-rest)`}>
        <BackArt uid={uid} shadowColor={shadowColor} withShadow />
      </g>
      <g clipPath={`url(#${uid}-strip)`} className={tearing ? 'tts-cards-tear' : undefined}>
        <BackArt uid={uid} shadowColor={shadowColor} withShadow />
        <line x1={20} y1={STRIP_H - 2} x2={480} y2={STRIP_H - 2} stroke={INK} strokeWidth={4} strokeDasharray="14 10" />
      </g>
    </svg>
  );
}
